import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useSocket } from '../../context/SocketContext';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LocationManager from '../../components/wms/LocationManager';
import WarehouseMap from './WarehouseMap';
import ProductAutocomplete from '../../components/common/ProductAutocomplete';
import PermissionGuard from '../../components/common/PermissionGuard';
import Pagination from '../../components/common/Pagination';
import SortableHeader from '../../components/common/SortableHeader';
import * as XLSX from 'xlsx';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';

// Helper for Leaflet resize in modal
const MapResizer = () => {
    const map = useMap();
    useEffect(() => {
        setTimeout(() => {
            map.invalidateSize();
        }, 400);
    }, [map]);
    return null;
};



// Fix default marker icons for Leaflet + Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const WarehouseManagement = () => {
    const { user } = useAuth();
    const { showToast, showConfirm } = useUI();
    const [warehouses, setWarehouses] = useState([]);
    const [stats, setStats] = useState(null);
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('Tous');
    const [showModal, setShowModal] = useState(false);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [selectedWarehouseForLocations, setSelectedWarehouseForLocations] = useState(null);
    const [editingWarehouse, setEditingWarehouse] = useState(null);
    const [searchSku, setSearchSku] = useState('');
    const [viewingWarehouseMap, setViewingWarehouseMap] = useState(null);
    const [showWorldMap, setShowWorldMap] = useState(false);
    const [mapRefreshKey, setMapRefreshKey] = useState(0);
    const [inventorySynthesis, setInventorySynthesis] = useState([]);
    const [inventoryViewMode, setInventoryViewMode] = useState('units'); // 'units' or 'percent'
    const [invCurrentPage, setInvCurrentPage] = useState(1);
    const [invTotalPages, setInvTotalPages] = useState(1);
    const [invTotalProducts, setInvTotalProducts] = useState(0);
    const [invLimit, setInvLimit] = useState(parseInt(localStorage.getItem('app_items_per_page')) || 25);
    const [invSortBy, setInvSortBy] = useState('name');
    const [invSortOrder, setInvSortOrder] = useState('ASC');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [suppliers, setSuppliers] = useState([]);


    // Address suggestions logic removed as it's now handled by AddressAutocomplete
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        region: '',
        typeId: '',
        maxCapacity: 1000,
        coordinates: null
    });

    const fetchMultiSiteInventory = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const params = new URLSearchParams({
                companyId: user.companyId,
                page: invCurrentPage,
                limit: invLimit,
                sortBy: invSortBy,
                sortOrder: invSortOrder
            });
            const res = await fetch(`/api/products/company/multi-site-inventory?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setInventorySynthesis(data.products || []);
                setInvTotalPages(data.totalPages || 1);
                setInvTotalProducts(data.total || 0);
            }
        } catch (error) {
            console.error('Fetch multi-site inventory error:', error);
            showToast("Erreur lors du chargement de l'inventaire multi-site", "error");
        }
    }, [user?.companyId, invCurrentPage, invLimit, invSortBy, invSortOrder, showToast]);

    const fetchData = useCallback(async () => {
        if (!user?.companyId) return;
        try {
            const [wRes, sRes, tRes, cRes, supRes] = await Promise.all([
                fetch(`/api/warehouses?companyId=${user.companyId}`),
                fetch(`/api/warehouses/stats?companyId=${user.companyId}`),
                fetch(`/api/warehouse-types?companyId=${user.companyId}`),
                fetch(`/api/companies/${user.companyId}`),
                fetch(`/api/suppliers?companyId=${user.companyId}&limit=1000`)
            ]);

            if (wRes.ok) setWarehouses(await wRes.json());
            if (sRes.ok) setStats(await sRes.json());
            if (tRes.ok) setTypes(await tRes.json());
            if (cRes.ok) setCompanyInfo(await cRes.json());
            if (supRes.ok) {
                const data = await supRes.json();
                setSuppliers(data.suppliers || []);
            }

        } catch (error) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, [user?.companyId, showToast]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        fetchMultiSiteInventory();
    }, [fetchMultiSiteInventory]);

    // Automatically select the first warehouse for the dynamic plan if none is selected
    useEffect(() => {
        if (!viewingWarehouseMap && warehouses.length > 0) {
            setViewingWarehouseMap(warehouses[0]);
        }
    }, [warehouses, viewingWarehouseMap]);

    const handleGeocode = async (address) => {
        if (!address || address.length < 5) return null;
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
            const data = await response.json();
            if (data && data.length > 0) {
                return {
                    lat: parseFloat(data[0].lat),
                    lng: parseFloat(data[0].lon)
                };
            }
        } catch (error) {
            console.error("Geocoding error:", error);
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Automatic geocoding if location changed or coords missing
            let coords = formData.coordinates;
            if (!coords || editingWarehouse?.location !== formData.location) {
                const geo = await handleGeocode(formData.location);
                if (geo) coords = geo;
            }

            const method = editingWarehouse ? 'PUT' : 'POST';
            const url = editingWarehouse ? `/api/warehouses/${editingWarehouse.id}` : '/api/warehouses';

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...formData,
                    coordinates: coords,
                    companyId: user.companyId
                })
            });

            if (response.ok) {
                showToast(editingWarehouse ? "Entrepôt mis à jour" : "Entrepôt créé", "success");
                setShowModal(false);
                setEditingWarehouse(null);
                setFormData({ name: '', location: '', region: '', typeId: '', maxCapacity: 1000, coordinates: null });

                fetchData();
            } else {
                showToast("Erreur lors de l'enregistrement", "error");
            }
        } catch (error) {
            showToast("Erreur serveur", "error");
        }
    };

    const handleDelete = async (id) => {
        showConfirm({
            title: "Supprimer l'entrepôt ?",
            message: "Cette action est irréversible. Toutes les données de stock associées seront perdues.",
            onConfirm: async () => {
                try {
                    const response = await fetch(`/api/warehouses/${id}`, { method: 'DELETE' });
                    const result = await response.json();

                    if (response.ok) {
                        showToast("Entrepôt supprimé", "success");
                        fetchData();
                    } else {
                        showToast(result.error || "Erreur lors de la suppression", "error");
                    }
                } catch (error) {
                    showToast("Erreur serveur", "error");
                }
            }
        });
    };


    const filteredWarehouses = filterType === 'Tous'
        ? warehouses
        : warehouses.filter(w => w.warehouseType?.name === filterType);

    const handleExport = async () => {
        try {
            showToast("Génération de l'export Excel complet...", "info");

            // 1. Récupérer TOUT l'inventaire multi-site (sans pagination)
            const invParams = new URLSearchParams({
                companyId: user.companyId,
                page: 1,
                limit: 10000,
                sortBy: invSortBy,
                sortOrder: invSortOrder
            });
            const invRes = await fetch(`/api/products/company/multi-site-inventory?${invParams.toString()}`);
            const invDataFull = invRes.ok ? (await invRes.json()).products : inventorySynthesis;

            // 2. Récupérer TOUS les emplacements (rangements)
            const locRes = await fetch(`/api/locations?companyId=${user.companyId}`);
            const allLocations = locRes.ok ? await locRes.json() : [];

            // 3. Préparer les données
            // Sheet 1: Résumé Entrepôts
            const warehouseData = filteredWarehouses.map(w => ({
                'Nom': w.name,
                'Localisation': w.location,
                'Région': w.region,
                'Type': w.type?.name || 'Standard',
                'Statut': w.status,
                'Capacité Max': w.maxCapacity,
                'Occupation (unités)': w.currentUsage,
                '% Occupation': `${Math.round((w.currentUsage / w.maxCapacity) * 100)}%`,
                'Valeur Stock (€)': Number(w.totalStockValue),
                'SKUs Actifs': w.activeSKUs
            }));

            // Sheet 2: Répartition des Stocks (Multi-Sites)
            const inventoryData = invDataFull.map(item => {
                const row = {
                    'Article': item.name,
                    'SKU': item.sku,
                    'Catégorie': item.category?.name || 'Général',
                    'Quantité Totale': item.totalQuantity,
                    'Statut': item.status
                };

                // Ajouter une colonne par entrepôt
                warehouses.forEach(w => {
                    row[w.name] = item.warehouseQuantities[w.id] || 0;
                });

                return row;
            });

            // Sheet 3: Rangements (Emplacements)
            const locationsData = allLocations.map(l => {
                const warehouse = warehouses.find(w => w.id === l.warehouseId);
                return {
                    'Entrepôt': warehouse?.name || 'Inconnu',
                    'Nom Emplacement': l.name,
                    'Type': l.type,
                    'Zone/Allée': l.zone || ''
                };
            });

            const wb = XLSX.utils.book_new();

            const wsWarehouses = XLSX.utils.json_to_sheet(warehouseData);
            XLSX.utils.book_append_sheet(wb, wsWarehouses, 'Résumé Entrepôts');

            const wsInventory = XLSX.utils.json_to_sheet(inventoryData);
            XLSX.utils.book_append_sheet(wb, wsInventory, 'Répartition des Stocks');

            const wsLocations = XLSX.utils.json_to_sheet(locationsData);
            XLSX.utils.book_append_sheet(wb, wsLocations, 'Rangements');

            const date = new Date().toISOString().slice(0, 10);
            XLSX.writeFile(wb, `Export_Entrepots_Complet_${date}.xlsx`);

            showToast("Export complet réussi", "success");
        } catch (error) {
            console.error('Export error:', error);
            showToast("Erreur lors de l'exportation", "error");
        }
    };

    if (loading) return <div className="p-10 text-center text-slate-500 animate-pulse">Chargement des centres logistiques...</div>;

    return (
        <>
            {/* Header Section - Sticky */}
            <div className="sticky top-[-16px] lg:top-[-16px] z-30 bg-background-light/80 dark:bg-background-dark/80 backdrop-blur-md pt-4 pb-6 mb-2 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200/50 dark:border-slate-800/50">
                <div>
                    <h1 className="text-3xl font-black tracking-tight bg-gradient-to-r from-primary to-blue-500 bg-clip-text text-transparent drop-shadow-sm">Gestion Multi-Entrepôts</h1>
                    <p className="text-slate-500 dark:text-slate-400 mt-1">Surveillance en temps réel de vos centres logistiques</p>
                </div>
                <div className="flex gap-3">
                    <PermissionGuard permission="wh.view">
                        <button
                            onClick={() => setShowWorldMap(true)}
                            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-sm font-bold hover:bg-slate-50 dark:hover:bg-slate-700 transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                        >
                            <span className="material-symbols-outlined text-[20px]">public</span>
                            <span className="truncate md:inline hidden">Carte Mondiale</span>
                        </button>
                    </PermissionGuard>
                    <PermissionGuard permission="wh.manage">
                        <button
                            onClick={() => {
                                setEditingWarehouse(null);
                                setFormData({ name: '', location: '', region: '', typeId: '', maxCapacity: 1000, coordinates: null });
                                setShowModal(true);
                            }}
                            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-all shadow-lg shadow-primary/20"
                        >
                            <span className="material-symbols-outlined text-[20px]">add</span>
                            <span className="truncate md:inline hidden">Nouvel Entrepôt</span>
                        </button>
                    </PermissionGuard>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 rounded-lg font-semibold text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    >
                        <span className="material-symbols-outlined text-sm">file_download</span>
                        Exporter
                    </button>
                </div>
            </div>

            {/* Global KPI Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="size-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                            <span className="material-symbols-outlined">euro</span>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Valeur Totale du Stock</p>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">{Number(stats?.totalStockValue || 0).toLocaleString()} €</h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="size-12 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                            <span className="material-symbols-outlined">inventory_2</span>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Taux d'Occupation Global</p>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">
                                {warehouses.length > 0 ? Math.round((warehouses.reduce((acc, w) => acc + w.currentUsage, 0) / warehouses.reduce((acc, w) => acc + w.maxCapacity, 0)) * 100) : 0}%
                            </h3>
                        </div>
                    </div>
                </div>
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="size-12 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                            <span className="material-symbols-outlined">barcode_scanner</span>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">SKU Actifs (Total)</p>
                            <h3 className="text-2xl font-black text-slate-900 dark:text-white">{stats?.activeSKUs || 0}</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal de création/édition Premium */}
            {showModal && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-2xl flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                        {/* Premium Gradient Header */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                            <div className="relative z-10 flex justify-between items-start">
                                <div className="flex gap-4 items-center">
                                    <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                        <span className="material-symbols-outlined text-primary text-[28px]">
                                            {editingWarehouse ? 'edit_location' : 'add_location_alt'}
                                        </span>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                                            {editingWarehouse ? "Modifier l'Entrepôt" : "Nouvel Entrepôt"}
                                        </h3>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-medium mt-1 uppercase tracking-widest opacity-70">
                                            {editingWarehouse ? editingWarehouse.name : 'Configuration logistique'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                            {/* Section: Informations Générales */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="material-symbols-outlined text-primary/50 text-[18px]">info</span>
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Informations de base</h4>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Nom du centre logistique *</label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-base font-bold text-slate-900 dark:text-white transition-all placeholder:text-slate-300 dark:placeholder:text-slate-600"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Ex: Entrepôt Nord-Est"
                                    />
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Adresse / Localisation</label>
                                    <AddressAutocomplete
                                        value={formData.location}
                                        onChange={(val) => setFormData({ ...formData, location: val })}
                                        onSelect={(addr, coords) => setFormData({ ...formData, location: addr, coordinates: coords })}
                                        className="mb-1"
                                    />
                                    {formData.coordinates && (
                                        <p className="px-1 text-[10px] text-emerald-500 font-bold flex items-center gap-1 mt-2">
                                            <span className="material-symbols-outlined text-[14px]">check_circle</span>
                                            Coordonnées validées : {formData.coordinates.lat.toFixed(4)}, {formData.coordinates.lng.toFixed(4)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Section: Paramètres Logistiques */}
                            <div className="space-y-6 pt-4 border-t border-slate-100 dark:border-slate-800">
                                <div className="flex items-center gap-3 mb-2">
                                    <span className="material-symbols-outlined text-amber-500/50 text-[18px]">settings_suggest</span>
                                    <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Configuration & Capacité</h4>
                                </div>

                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Région</label>
                                        <div className="relative">
                                            <select
                                                required
                                                className={`w-full h-14 px-5 appearance-none bg-slate-50 dark:bg-slate-800 border-2 rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-bold text-slate-900 dark:text-white transition-all cursor-pointer ${!formData.region ? 'border-amber-500/30' : 'border-transparent'}`}
                                                value={formData.region}
                                                onChange={e => setFormData({ ...formData, region: e.target.value })}
                                            >
                                                <option value="">-- Choisir --</option>
                                                <option value="Europe">Europe</option>
                                                <option value="Amérique du Nord">Amérique du Nord</option>
                                                <option value="Asie">Asie</option>
                                            </select>
                                            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Type logistique</label>
                                        <div className="relative">
                                            <select
                                                required
                                                className={`w-full h-14 px-5 appearance-none bg-slate-50 dark:bg-slate-800 border-2 rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-bold text-slate-900 dark:text-white transition-all cursor-pointer ${!formData.typeId ? 'border-amber-500/30' : 'border-transparent'}`}
                                                value={formData.typeId}
                                                onChange={e => setFormData({ ...formData, typeId: e.target.value })}
                                            >
                                                <option value="">-- Choisir --</option>
                                                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                            </select>
                                            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">expand_more</span>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 px-1">Capacité Max (unités)</label>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            required
                                            className="w-full h-14 px-5 bg-slate-50 dark:bg-slate-800 border-2 border-transparent rounded-2xl outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary/30 text-sm font-black text-slate-900 dark:text-white transition-all"
                                            value={formData.maxCapacity}
                                            onChange={e => setFormData({ ...formData, maxCapacity: parseInt(e.target.value) })}
                                        />
                                        <span className="absolute right-4 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-300">inventory_2</span>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 flex gap-4 shrink-0 border-t border-slate-100 dark:border-slate-800">
                                <button type="button" onClick={() => setShowModal(false)} className="flex-1 h-12 rounded-2xl text-slate-500 font-black uppercase tracking-widest hover:bg-slate-100 dark:hover:bg-slate-800 transition-all text-xs">Annuler</button>
                                <button type="submit" className="flex-1 h-12 rounded-2xl bg-primary text-white font-black uppercase tracking-widest hover:bg-primary/90 transition-all shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] text-xs flex items-center justify-center gap-2">
                                    <span className="material-symbols-outlined text-[18px]">{editingWarehouse ? 'save' : 'add_circle'}</span>
                                    {editingWarehouse ? 'Mettre à jour' : 'Créer l\'entrepôt'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-8 pb-2 overflow-x-auto">
                <button
                    onClick={() => setFilterType('Tous')}
                    className={`flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-all ${filterType === 'Tous' ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary'}`}
                >
                    Tous les types
                </button>
                {types.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setFilterType(t.name)}
                        className={`flex h-9 items-center gap-2 rounded-lg px-4 text-sm font-medium transition-all ${filterType === t.name ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:border-primary'}`}
                    >
                        <span className={`material-symbols-outlined text-${t.color}-500 text-sm`}>{t.icon}</span>
                        {t.name}
                    </button>
                ))}
            </div>

            {/* Warehouse Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {filteredWarehouses.map(w => (
                    <div key={w.id} className="group bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow relative overflow-hidden">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-bold text-lg text-slate-900 dark:text-white">{w.name}</h3>
                                <p className="text-xs text-slate-500 flex items-center gap-1">
                                    <span className="material-symbols-outlined text-[14px]">location_on</span>
                                    {w.location}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-[10px] font-bold rounded uppercase ${w.status === 'Opérationnel' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'}`}>
                                    {w.status}
                                </span>
                            </div>
                        </div>

                        <div className="mb-6">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-slate-500">Capacité</span>
                                <span className={`font-bold ${((w.currentUsage / w.maxCapacity) * 100) > 90 ? 'text-amber-500' : 'text-slate-900 dark:text-white'}`}>
                                    {Math.round((w.currentUsage / w.maxCapacity) * 100)}%
                                </span>
                            </div>
                            <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2">
                                <div className={`h-2 rounded-full ${((w.currentUsage / w.maxCapacity) * 100) > 90 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${(w.currentUsage / w.maxCapacity) * 100}%` }}></div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                            <div>
                                <p className="text-xs text-slate-500 uppercase font-semibold">Valeur Stock</p>
                                <p className="text-xl font-black text-slate-900 dark:text-white">{Number(w.totalStockValue).toLocaleString()} €</p>
                            </div>
                            <div className="flex justify-between items-end">
                                <div>
                                    <p className="text-xs text-slate-500 uppercase font-semibold">SKU Actifs</p>
                                    <p className="text-xl font-black text-slate-900 dark:text-white">{w.activeSKUs}</p>
                                </div>
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <PermissionGuard permission="wh.view">
                                        <button
                                            onClick={() => { setSelectedWarehouseForLocations(w); setShowLocationModal(true); }}
                                            className="p-1.5 text-slate-400 hover:text-primary transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">location_on</span>
                                        </button>
                                    </PermissionGuard>
                                    <PermissionGuard permission="wh.manage">
                                        <button
                                            onClick={() => { setEditingWarehouse(w); setFormData({ ...w }); setShowModal(true); }}
                                            className="p-1.5 text-slate-400 hover:text-primary transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">edit</span>
                                        </button>
                                        <button
                                            onClick={() => handleDelete(w.id)}
                                            className="p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                                        >
                                            <span className="material-symbols-outlined text-[20px]">delete</span>
                                        </button>
                                    </PermissionGuard>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>


            {/* Visual Section: Map & Global Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                {/* Plan Interactif de l'Entrepôt */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden shadow-sm flex flex-col min-h-[500px]">
                    <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center text-sm">
                        <div className="flex items-center gap-4">
                            <h3 className="font-bold text-slate-900 dark:text-white">Plan Dynamique</h3>
                            <button onClick={() => setShowWorldMap(true)} className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold hover:bg-primary/10 hover:text-primary transition-all flex items-center gap-1.5">
                                <span className="material-symbols-outlined text-sm">public</span>
                                Carte Mondiale
                            </button>
                            <select
                                className="bg-slate-50 dark:bg-slate-800 border-2 border-slate-100 dark:border-slate-700 rounded-lg px-3 py-1.5 font-bold text-xs outline-none focus:ring-2 focus:ring-primary"
                                onChange={(e) => setViewingWarehouseMap(warehouses.find(w => w.id === parseInt(e.target.value)))}
                                value={viewingWarehouseMap?.id || ''}
                            >
                                <option value="">-- Choisir un entrepôt --</option>
                                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                            </select>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-64">
                                <ProductAutocomplete
                                    value={searchSku}
                                    onChange={(val) => setSearchSku(val)}
                                    onSelect={(p) => setSearchSku(p.name)}
                                    companyId={user?.companyId}
                                    warehouseId={viewingWarehouseMap?.id}
                                    placeholder="Chercher un produit..."
                                />


                            </div>
                            <div className="flex gap-2 text-[10px] text-slate-500">
                                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-primary"></div> Racks</span>
                                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-rose-500"></div> Présent</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 min-h-[400px]">
                        {viewingWarehouseMap ? (
                            <WarehouseMap key={mapRefreshKey} warehouseId={viewingWarehouseMap.id} highlightSku={searchSku} />
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 p-12 text-center">
                                <span className="material-symbols-outlined text-4xl mb-4 text-slate-200">map</span>
                                <p className="font-medium">Sélectionnez un entrepôt pour visualiser son plan interactif.</p>
                                <p className="text-xs mt-1">Disposition des allées et localisation des produits.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Global Capacity Chart / Breakdown */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm">

                    <h3 className="font-bold text-slate-900 dark:text-white mb-6">Répartition des Stocks</h3>
                    <div className="space-y-6">
                        {stats?.distribution && stats.distribution.length > 0 ? stats.distribution.map(item => (
                            <div key={item.name}>
                                <div className="flex justify-between items-center mb-2 text-xs">
                                    <span className="font-medium text-slate-600 dark:text-slate-400">{item.name}</span>
                                    <span className="font-bold text-slate-900 dark:text-white">
                                        {stats.totalStockValue > 0 ? ((item.value / stats.totalStockValue) * 100).toFixed(1) : 0}% ({Number(item.value).toLocaleString()} €)
                                    </span>
                                </div>
                                <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-3">
                                    <div className="h-3 rounded-full" style={{ backgroundColor: item.color, width: `${stats.totalStockValue > 0 ? (item.value / stats.totalStockValue) * 100 : 0}%` }}></div>
                                </div>
                            </div>
                        )) : (
                            <div className="text-center py-10 text-slate-400 text-sm italic">
                                Aucune donnée de catégorie disponible.
                            </div>
                        )}
                    </div>
                    <div className="mt-8 pt-8 border-t border-slate-100 dark:border-slate-800 text-center">
                        <p className="text-3xl font-black text-slate-900 dark:text-white">{stats?.totalStockValue.toLocaleString()} €</p>
                        <p className="text-sm text-slate-500">Valeur Totale Approuvée</p>
                        <div className="flex items-center justify-center gap-1 text-green-500 text-xs mt-2 font-bold">
                            <span className="material-symbols-outlined text-[14px]">trending_up</span>
                            {stats?.monthlyProgress} ce mois
                        </div>
                    </div>
                </div >
            </div >

            {/* Comparison Table */}
            < div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm overflow-hidden mb-10" >
                <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <h3 className="font-bold text-slate-900 dark:text-white">Synthèse de Disponibilité Multi-Sites</h3>
                    <div className="flex gap-2">
                        <div className="flex rounded-lg bg-slate-100 dark:bg-slate-800 p-1">
                            <button
                                onClick={() => setInventoryViewMode('units')}
                                className={`px-3 py-1 text-xs font-bold rounded transition-all ${inventoryViewMode === 'units' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Unités
                            </button>
                            <button
                                onClick={() => setInventoryViewMode('percent')}
                                className={`px-3 py-1 text-xs font-bold rounded transition-all ${inventoryViewMode === 'percent' ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-900 dark:text-white' : 'text-slate-500 dark:text-slate-400'}`}
                            >
                                Pourcentage
                            </button>
                        </div>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left table-fixed min-w-[1000px]">
                        <thead className="bg-slate-50 dark:bg-slate-950/50 text-slate-500 dark:text-slate-400 text-[10px] uppercase font-bold tracking-wider">
                            <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-800 text-left text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                <SortableHeader
                                    label="Article"
                                    field="name"
                                    currentSort={invSortBy}
                                    currentOrder={invSortOrder}
                                    onSort={(f, o) => { setInvSortBy(f); setInvSortOrder(o); }}
                                    className="px-6 py-4 w-[300px]"
                                />
                                <SortableHeader
                                    label="Qté Totale"
                                    field="totalQuantity"
                                    currentSort={invSortBy}
                                    currentOrder={invSortOrder}
                                    onSort={(f, o) => { setInvSortBy(f); setInvSortOrder(o); }}
                                    className="px-6 py-4 w-[120px]"
                                />
                                {warehouses.map(w => (
                                    <SortableHeader
                                        key={w.id}
                                        label={w.name}
                                        field={`warehouse:${w.id}`}
                                        currentSort={invSortBy}
                                        currentOrder={invSortOrder}
                                        onSort={(f, o) => { setInvSortBy(f); setInvSortOrder(o); }}
                                        className="px-6 py-4 whitespace-nowrap w-[150px]"
                                    />
                                ))}
                                <th className="px-6 py-4 text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-wider w-[150px]">Alerte</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {inventorySynthesis.length > 0 ? inventorySynthesis.map((item, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="size-8 rounded flex items-center justify-center" style={{ backgroundColor: (item.category?.color || '#94a3b8') + '20' }}>
                                                <span className="material-symbols-outlined text-sm" style={{ color: item.category?.color || '#94a3b8' }}>inventory</span>
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{item.name}</p>
                                                <p className="text-[10px] text-slate-500">{item.sku}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 font-semibold text-sm text-slate-900 dark:text-white">{item.totalQuantity}</td>
                                    {warehouses.map(w => {
                                        const qty = item.warehouseQuantities[w.id] || 0;
                                        const displayValue = inventoryViewMode === 'units'
                                            ? qty
                                            : (item.totalQuantity > 0 ? `${((qty / item.totalQuantity) * 100).toFixed(0)}%` : '0%');

                                        return (
                                            <td key={w.id} className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400 font-medium">
                                                {displayValue}
                                            </td>
                                        );
                                    })}
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center gap-1.5 py-1 px-2.5 rounded-full text-xs font-medium ${item.status === 'Stable' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                                            item.status === 'Critique' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                                                'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                            }`}>
                                            {item.status}
                                        </span>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={3 + warehouses.length} className="px-6 py-10 text-center text-slate-400 italic text-sm">
                                        Aucun produit trouvé dans l'inventaire.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    currentPage={invCurrentPage}
                    totalPages={invTotalPages}
                    onPageChange={setInvCurrentPage}
                    limit={invLimit}
                    onLimitChange={(l) => {
                        setInvLimit(l);
                        localStorage.setItem('app_items_per_page', l);
                        setInvCurrentPage(1);
                    }}
                    totalItems={invTotalProducts}
                />
            </div>

            {/* Location Manager Modal */}
            {showLocationModal && selectedWarehouseForLocations && (
                <LocationManager
                    warehouse={selectedWarehouseForLocations}
                    onUpdate={() => setMapRefreshKey(k => k + 1)}

                    onClose={() => {
                        setShowLocationModal(false);
                        setSelectedWarehouseForLocations(null);
                        setMapRefreshKey(k => k + 1);
                    }}
                />
            )}

            {/* Modale Carte Mondiale Leaflet Premium */}
            {showWorldMap && (
                <div className="fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-slate-900 rounded-[2.5rem] shadow-2xl shadow-primary/20 w-full max-w-6xl flex flex-col max-h-[95vh] border border-slate-200 dark:border-slate-800 overflow-hidden animate-in zoom-in-95 duration-500">

                        {/* Premium Header with Gradient */}
                        <div className="shrink-0 px-8 py-6 relative overflow-hidden bg-gradient-to-br from-primary/10 via-primary/5 to-transparent dark:from-primary/20 dark:via-primary/10 dark:to-transparent border-b border-primary/10 dark:border-primary/20">
                            <div className="relative z-10 flex justify-between items-center">
                                <div className="flex gap-4 items-center">
                                    <div className="size-12 rounded-2xl bg-primary/10 flex items-center justify-center shadow-inner">
                                        <span className="material-symbols-outlined text-primary text-[28px]">public</span>
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-black text-slate-900 dark:text-white leading-tight">Carte Mondiale Collaborative</h2>
                                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-[0.2em] opacity-70">
                                            {warehouses.length} sites actifs • {suppliers.length} partenaires validés
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setShowWorldMap(false)}
                                    className="size-10 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800 shadow-sm hover:shadow transition-all"
                                >
                                    <span className="material-symbols-outlined">close</span>
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 relative bg-slate-100 dark:bg-slate-800 min-h-[500px]">
                            <style>{`
                                .leaflet-container {
                                    width: 100% !important;
                                    height: 100% !important;
                                    z-index: 1 !important;
                                    font-family: inherit !important;
                                }
                                .premium-popup .leaflet-popup-content-wrapper {
                                    background: rgba(255, 255, 255, 0.95);
                                    backdrop-filter: blur(8px);
                                    border-radius: 20px;
                                    padding: 8px;
                                    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
                                    border: 1px solid rgba(255, 255, 255, 0.2);
                                }
                                .dark .premium-popup .leaflet-popup-content-wrapper {
                                    background: rgba(15, 23, 42, 0.9);
                                    border: 1px solid rgba(255, 255, 255, 0.1);
                                }
                                .premium-popup .leaflet-popup-content {
                                    margin: 12px;
                                }
                                .premium-popup .leaflet-popup-tip {
                                    background: rgba(255, 255, 255, 0.95);
                                }
                                .dark .premium-popup .leaflet-popup-tip {
                                    background: rgba(15, 23, 42, 0.9);
                                }
                            `}</style>
                            <MapContainer
                                center={warehouses.find(w => w.coordinates)?.coordinates ? [warehouses.find(w => w.coordinates).coordinates.lat, warehouses.find(w => w.coordinates).coordinates.lng] : [46.6, 2.3]}
                                zoom={5}
                                style={{ height: '500px', width: '100%', minHeight: '500px' }}
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <MapResizer />
                                {warehouses.map(w => {
                                    if (!w.coordinates) return null;
                                    const typeColor = w.warehouseType?.color || '#ec5b13';
                                    const typeIcon = w.warehouseType?.icon || 'warehouse';
                                    const customIcon = L.divIcon({
                                        className: '',
                                        html: `<div style="background:${typeColor};width:38px;height:38px;border-radius:12px;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.3);transform:translate(-50%, -50%)"><span class="material-symbols-outlined" style="color:white;font-size:22px">${typeIcon}</span></div>`,
                                        iconSize: [38, 38],
                                        iconAnchor: [19, 38],
                                        popupAnchor: [0, -38]
                                    });
                                    const usage = Math.round((w.currentUsage / w.maxCapacity) * 100);
                                    return (
                                        <Marker key={w.id} position={[w.coordinates.lat, w.coordinates.lng]} icon={customIcon}>
                                            <Popup className="premium-popup">
                                                <div className="p-1 min-w-[220px]">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <div className="size-10 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: typeColor + '20' }}>
                                                            <span className="material-symbols-outlined" style={{ color: typeColor, fontSize: '20px' }}>{typeIcon}</span>
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-slate-900 text-sm leading-tight">{w.name}</p>
                                                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{w.warehouseType?.name || 'Standard'}</p>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-3">
                                                        <div>
                                                            <div className="flex justify-between text-[10px] mb-1 font-bold">
                                                                <span className="text-slate-400 uppercase">Occupation</span>
                                                                <span className={usage > 90 ? 'text-amber-500' : 'text-slate-900'}>{usage}%</span>
                                                            </div>
                                                            <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden">
                                                                <div
                                                                    className={`h-full rounded-full transition-all duration-1000 ${usage > 90 ? 'bg-amber-500' : 'bg-primary'}`}
                                                                    style={{ width: `${usage}%` }}
                                                                ></div>
                                                            </div>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4 py-3 border-t border-slate-50">
                                                            <div>
                                                                <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Stock</span>
                                                                <span className="text-xs font-black text-slate-900">{Number(w.totalStockValue).toLocaleString()} €</span>
                                                            </div>
                                                            <div>
                                                                <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Région</span>
                                                                <span className="text-xs font-black text-slate-900">{w.region}</span>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2 text-primary font-bold text-[10px] pt-1">
                                                            <span className="material-symbols-outlined text-[14px]">location_on</span>
                                                            <span className="truncate">{w.location}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    );
                                })}

                                {suppliers.map(s => {
                                    if (!s.coordinates) return null;
                                    const supplierIcon = L.divIcon({
                                        className: '',
                                        html: `<div style="background:#10b981;width:38px;height:38px;border-radius:12px;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 4px 12px rgba(16,185,129,0.3);transform:translate(-50%, -50%)"><span class="material-symbols-outlined" style="color:white;font-size:20px">local_shipping</span></div>`,
                                        iconSize: [38, 38],
                                        iconAnchor: [19, 38],
                                        popupAnchor: [0, -38]
                                    });
                                    return (
                                        <Marker key={`sup-${s.id}`} position={[s.coordinates.lat, s.coordinates.lng]} icon={supplierIcon}>
                                            <Popup className="premium-popup">
                                                <div className="p-1 min-w-[220px]">
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <div className="size-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                                                            <span className="material-symbols-outlined text-emerald-500 text-[20px]">local_shipping</span>
                                                        </div>
                                                        <div>
                                                            <p className="font-black text-slate-900 text-sm leading-tight">{s.name}</p>
                                                            <p className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">Partenaire</p>
                                                        </div>
                                                    </div>

                                                    <p className="text-[10px] text-slate-500 mb-3 leading-relaxed">{s.address}</p>

                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-3 py-3 border-t border-slate-50">
                                                        <div>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Catégorie</span>
                                                            <span className="text-xs font-black text-slate-900">{s.category || 'N/A'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Score</span>
                                                            <span className="text-xs font-black text-emerald-600">{s.deliveryScore}/5</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Délai</span>
                                                            <span className="text-xs font-black text-slate-900">{s.avgDeliveryTime || 'N/A'}</span>
                                                        </div>
                                                        <div>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase block mb-0.5">Statut</span>
                                                            <span className="text-[10px] font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">{s.status}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    );
                                })}

                                {companyInfo && companyInfo.lat && companyInfo.lng && (
                                    <Marker
                                        position={[companyInfo.lat, companyInfo.lng]}
                                        icon={L.divIcon({
                                            className: '',
                                            html: `<div style="background:#4f46e5;width:46px;height:46px;border-radius:14px;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 6px 16px rgba(79,70,229,0.4);transform:translate(-50%, -50%)"><span class="material-symbols-outlined" style="color:white;font-size:26px">corporate_fare</span></div>`,
                                            iconSize: [46, 46],
                                            iconAnchor: [23, 46],
                                            popupAnchor: [0, -46]
                                        })}
                                    >
                                        <Popup className="premium-popup">
                                            <div className="p-1 min-w-[240px]">
                                                <div className="flex items-center gap-4 mb-4">
                                                    <div className="size-12 rounded-2xl bg-indigo-50 flex items-center justify-center shrink-0 border border-indigo-100">
                                                        <span className="material-symbols-outlined text-indigo-600 text-[24px]">corporate_fare</span>
                                                    </div>
                                                    <div>
                                                        <p className="font-black text-slate-900 text-base leading-tight">{companyInfo.name}</p>
                                                        <p className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mt-0.5">Siège Social</p>
                                                    </div>
                                                </div>

                                                <div className="space-y-3 mb-2">
                                                    <div className="flex gap-3">
                                                        <span className="material-symbols-outlined text-slate-300 text-[18px]">location_on</span>
                                                        <p className="text-[11px] text-slate-600 leading-relaxed font-medium">
                                                            {companyInfo.address}<br />
                                                            <span className="font-black text-slate-900">{companyInfo.zipCode} {companyInfo.city}</span>
                                                        </p>
                                                    </div>
                                                    {companyInfo.phone && (
                                                        <div className="flex gap-3 items-center">
                                                            <span className="material-symbols-outlined text-slate-300 text-[18px]">phone</span>
                                                            <span className="text-[11px] text-slate-600 font-black">{companyInfo.phone}</span>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-4 pt-3 border-t border-slate-50 text-center">
                                                    <span className="text-[9px] font-black text-slate-300 uppercase tracking-[0.2em]">StockManager Pro Infrastructure</span>
                                                </div>
                                            </div>
                                        </Popup>
                                    </Marker>
                                )}
                            </MapContainer>
                        </div>
                    </div>
                </div>
            )}
        </>

    );
};

export default WarehouseManagement;
